import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useSocial } from "../../contexts/SocialContext";
import { MessageCircle, X, Send, Globe, Circle, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SocialChatWidget = () => {
  const { user } = useAuth();
  const {
    connected, onlineCount, isOnline, worldMessages, dmThreads, unreadWorld, unreadDm, totalUnread,
    sendWorld, sendDm, loadWorldHistory, loadDmHistory, setActiveThread, openProfile,
  } = useSocial();

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("world"); // 'world' | friendId
  const [friends, setFriends] = useState([]);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const fetchFriends = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/friends`, { withCredentials: true });
      setFriends(res.data.friends || []);
    } catch (e) { /* ignore */ }
  }, []);

  // When opening, load world history + friends
  useEffect(() => {
    if (open) {
      fetchFriends();
      loadWorldHistory();
      setActiveThread(tab);
    } else {
      setActiveThread(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When switching tabs, mark active + load dm history
  useEffect(() => {
    if (!open) return;
    setActiveThread(tab);
    if (tab !== "world") loadDmHistory(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, open]);

  const messages = tab === "world" ? worldMessages : (dmThreads[tab] || []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, tab]);

  if (!user) return null;

  const handleSend = () => {
    const t = text.trim();
    if (!t) return;
    if (tab === "world") sendWorld(t);
    else sendDm(tab, t);
    setText("");
  };

  const activeFriend = tab !== "world" ? friends.find((f) => f.user_id === tab) : null;

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="social-chat-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-24 right-4 z-[60] bg-sky-500 text-white border-4 border-slate-950 rounded-2xl w-14 h-14 flex items-center justify-center cartoon-shadow hover:scale-105 transition-transform"
        title="Chat"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!open && totalUnread > 0 && (
          <span data-testid="social-unread-badge" className="absolute -top-1 -right-1 bg-rose-500 text-white border-2 border-slate-950 rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center text-[10px] font-bold">
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div data-testid="social-chat-panel" className="fixed bottom-40 right-4 z-[60] w-[22rem] max-w-[calc(100vw-2rem)] h-[28rem] max-h-[calc(100vh-12rem)] bg-slate-800 border-4 border-slate-950 rounded-3xl cartoon-shadow-lg-static flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-3 py-2 bg-slate-900 border-b-4 border-slate-950 flex items-center justify-between">
            <span className="font-display font-bold text-sm flex items-center gap-1.5">
              <MessageCircle className="w-4 h-4 text-sky-400" /> Chat
            </span>
            <span data-testid="online-count" className="text-xs text-emerald-300 font-bold flex items-center gap-1">
              <Circle className={`w-2.5 h-2.5 ${connected ? "text-emerald-400 fill-emerald-400" : "text-slate-600 fill-slate-600"}`} /> {onlineCount} en ligne
            </span>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 p-2 overflow-x-auto bg-slate-850 border-b-2 border-slate-950">
            <button data-testid="chat-tab-world" onClick={() => setTab("world")}
              className={`relative flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border-2 border-slate-950 whitespace-nowrap ${tab === "world" ? "bg-sky-500 text-white" : "bg-slate-900 text-slate-300"}`}>
              <Globe className="w-3.5 h-3.5" /> Monde
              {unreadWorld > 0 && tab !== "world" && <span className="absolute -top-1 -right-1 bg-rose-500 text-white rounded-full w-4 h-4 text-[9px] flex items-center justify-center border border-slate-950">{unreadWorld}</span>}
            </button>
            {friends.length === 0 ? (
              <span className="text-[11px] text-slate-500 flex items-center gap-1 px-2"><Users className="w-3 h-3" /> Ajoute des amis pour discuter en privé</span>
            ) : friends.map((f) => {
              const u = unreadDm[f.user_id] || 0;
              return (
                <button key={f.user_id} data-testid={`chat-tab-friend-${f.user_id}`} onClick={() => setTab(f.user_id)}
                  className={`relative flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border-2 border-slate-950 whitespace-nowrap ${tab === f.user_id ? "bg-fuchsia-500 text-white" : "bg-slate-900 text-slate-300"}`}>
                  <Circle className={`w-2 h-2 ${isOnline(f.user_id) ? "text-emerald-400 fill-emerald-400" : "text-slate-600 fill-slate-600"}`} />
                  {(f.name || "Ami").split(" ")[0]}
                  {u > 0 && tab !== f.user_id && <span className="absolute -top-1 -right-1 bg-rose-500 text-white rounded-full w-4 h-4 text-[9px] flex items-center justify-center border border-slate-950">{u}</span>}
                </button>
              );
            })}
          </div>

          {/* Messages */}
          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-800">
            {activeFriend && (
              <button onClick={() => openProfile(activeFriend.user_id)} className="text-[11px] text-fuchsia-300 underline mb-1">Voir le profil de {activeFriend.name}</button>
            )}
            {messages.length === 0 ? (
              <p className="text-center text-slate-500 text-xs py-6">{tab === "world" ? "Sois le premier à écrire dans le chat mondial !" : "Aucun message. Dis bonjour 👋"}</p>
            ) : messages.map((m, i) => {
              const isMine = tab === "world" ? m.user_id === user.user_id : m.from === user.user_id;
              const author = tab === "world" ? m.name : (isMine ? "Toi" : m.from_name);
              const authorId = tab === "world" ? m.user_id : m.from;
              return (
                <div key={i} className={`flex flex-col ${isMine ? "items-end" : "items-start"}`}>
                  {!isMine && (
                    <button onClick={() => authorId && openProfile(authorId)} className="text-[10px] text-slate-400 font-bold hover:text-sky-300 px-1">{author}</button>
                  )}
                  <div className={`max-w-[80%] px-3 py-1.5 rounded-2xl text-sm border-2 border-slate-950 ${isMine ? "bg-sky-500 text-white" : "bg-slate-900 text-slate-100"}`}>
                    {m.text}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Input */}
          <div className="p-2 border-t-4 border-slate-950 bg-slate-900 flex gap-2">
            <input
              data-testid="chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSend(); }}
              placeholder={tab === "world" ? "Message au monde…" : "Message privé…"}
              maxLength={400}
              disabled={!connected}
              className="flex-1 bg-slate-800 border-2 border-slate-700 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500"
            />
            <button data-testid="chat-send-btn" onClick={handleSend} disabled={!connected || !text.trim()}
              className="bg-sky-500 text-white border-2 border-slate-950 rounded-xl px-3 cartoon-shadow disabled:opacity-40 flex items-center justify-center">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
